/**
 * TrackRow Component
 * Individual track row with selection, audio features, and drag-and-drop
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, GripVertical, Play, Pause, Music, ListMusic, Copy, Bot, ChevronRight } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { TrackWithInstanceId, AudioFeatures } from '../../api/types';
import { useSelectionStore } from '../../stores/selection';
import { useWorkbenchStore } from '../../stores/workbench';
import { useSequencerStore } from '../../stores/sequencer';
import { usePlayerUIStore } from '../../stores/player';
import { usePlaybackQueueStore } from '../../stores/playbackQueue';
import { usePlayerControls, usePlaybackState } from '../../api/hooks';
import { formatBpm, formatDuration, formatPlayedAt } from '../../lib/audio-features';
import { toCamelot } from '../../lib/camelot';
import { CamelotKeyBadge } from '../shared/CamelotKeyBadge';
import { EnergyBar } from '../shared/EnergyBar';
import { LikeButton } from './LikeButton';
import { usePasteOperations } from '../../hooks/usePasteOperations';
import { toast } from '../ui/Toast';
import { notifyRateLimitedFromError } from '../../lib/rateLimitNotice';
import { askAIService } from '../../api/services/askAI';
import { buildSmartPlaybackParams } from '../../lib/keyboard-helpers';

interface TrackRowProps {
  mode?: 'workbench' | 'history';
  track: TrackWithInstanceId;
  position: number;
  audioFeatures?: AudioFeatures | null;
  allTrackIds: string[];
  isDragging?: boolean;
  currentTrackId?: string | null;
  isJuggleSource?: boolean;
  playedAt?: string; // ISO timestamp for history mode
  isHistorySelected?: boolean; // Selection state for history mode
  onHistoryCheckboxToggle?: (trackId: string) => void; // Callback for history mode checkbox
}

export function TrackRow({
  mode = 'workbench',
  track,
  position,
  audioFeatures,
  allTrackIds,
  currentTrackId,
  isJuggleSource = false,
  playedAt,
  isHistorySelected = false,
  onHistoryCheckboxToggle,
}: TrackRowProps) {
  const { isSelected, toggleSelection, selectRange, lastSelectedId, selectOnly } = useSelectionStore();
  const { removeTrack, tracks, activePlaylist, addAISlot, capabilities } = useWorkbenchStore();
  const { play, pause } = usePlayerControls();
  const { pasteSong, pastePlaylist } = usePasteOperations();

  // Context menu state
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showAskAISubmenu, setShowAskAISubmenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const {
    isPlayerActive,
    setIsPlayerActive,
  } = usePlayerUIStore();
  const playbackQueue = usePlaybackQueueStore();
  const { previewMode, setHookTimer, clearHookTimer } = playbackQueue;
  const { isOpen: isSequencerOpen } = useSequencerStore();

  // Get playback state to check if device is available
  const { data: playbackState } = usePlaybackState({ enabled: isPlayerActive });
  const isPlaying = playbackState?.isPlaying ?? false;

  const selected = isSelected(track.instanceId);
  const isCurrentTrack = currentTrackId === track.id;

  // Sortable drag and drop for reordering (disabled in history mode)
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: track.instanceId,
    disabled: mode === 'history', // Disable drag in history mode
  });

  // Droppable zone for external tracks AND playlists - insert BELOW this track
  // Disabled when sequencer overlay is open or in history mode
  const { setNodeRef: setDroppableRef, isOver: isDropOver } = useDroppable({
    id: `track-drop-${track.instanceId}`,
    data: {
      type: 'track-drop-zone',
      position: position, // Insert at this position (below this track)
    },
    disabled: isSequencerOpen || mode === 'history',
  });

  // Combine refs
  const setNodeRef = (node: HTMLElement | null) => {
    setSortableRef(node);
    setDroppableRef(node);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };

  // Handle context menu
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to TrackTable
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  // Handle paste song from clipboard
  const handlePasteSong = async () => {
    setShowContextMenu(false);
    await pasteSong(position); // Insert at current position
  };

  // Handle paste playlist from clipboard
  const handlePastePlaylist = async () => {
    setShowContextMenu(false);
    await pastePlaylist(position); // Insert at current position
  };

  // Handle copy track URL to clipboard
  const handleCopyToClipboard = async () => {
    setShowContextMenu(false);

    try {
      // Convert spotify:track:abc123 to https://open.spotify.com/track/abc123
      const trackId = track.uri.replace('spotify:track:', '');
      const trackUrl = `https://open.spotify.com/track/${trackId}`;

      await navigator.clipboard.writeText(trackUrl);
      console.log('Copied track URL to clipboard:', trackUrl);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard. Please try again.');
    }
  };

  // Handle Ask AI with count
  const handleAskAIWithCount = async (count: number) => {
    setShowContextMenu(false);
    setShowAskAISubmenu(false);

    if (!activePlaylist) {
      toast.error('No active playlist');
      return;
    }

    // Show loading toast
    const loadingToastId = toast.loading('Asking AI for suggestions...');

    try {
      // Call backend API
      const response = await askAIService.generateTracks({
        playlistId: activePlaylist.id,
        position: position,
        trackCount: count,
        promptType: 'auto', // Auto-detect from playlist name
      });

      // Dismiss loading toast
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
      }

      // Handle partial matches
      if (response.matchStats.found === 0) {
        toast.error('No tracks found on Spotify. Try again.');
        return;
      }

      // Always add the tracks, even if fewer than requested
      addAISlot(position, response.tracks, response.promptUsed);

      if (response.matchStats.found < response.matchStats.requested) {
        // Show warning but tracks are already added
        toast.warning(
          `Only found ${response.matchStats.found}/${response.matchStats.requested} tracks`,
          { duration: 5000 }
        );
      } else {
        // All tracks found
        toast.success(`AI suggested ${response.tracks.length} tracks`);
      }
    } catch (error: any) {
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
      }

      if (error.status === 429) {
        notifyRateLimitedFromError(error.details);
      } else if (error.status === 422) {
        // Parse error - show details
        toast.error('Could not parse AI response. Check console for details.');
        console.error('LLM parse error:', error);
      } else if (error.status === 500) {
        toast.error('AI service unavailable. Please try again later.');
      } else if (error.status === 404) {
        toast.error('Playlist not found.');
      } else {
        toast.error('Failed to generate suggestions. Please try again.');
        console.error('Ask AI error:', error);
      }
    }
  };

  // Handle row click with keyboard modifiers
  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger selection if clicking the remove button or play button
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    if (e.shiftKey && lastSelectedId) {
      // Shift+Click: Range select
      selectRange(lastSelectedId, track.instanceId, allTrackIds);
    } else if (e.metaKey || e.ctrlKey) {
      // Cmd/Ctrl+Click: Multi-select
      toggleSelection(track.instanceId);
    } else {
      // Normal click: SELECT ONLY THIS (OS-standard)
      selectOnly(track.instanceId);
    }
  };

  // Handle double-click to play track
  // Note: usePlayerControls hook automatically shows DeviceSelector on NO_ACTIVE_DEVICE error
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Activate player UI (enables polling)
    setIsPlayerActive(true);

    // Play single track (preview mode determines full vs hook)
    playbackQueue.clear();
    playbackQueue.setQueue(
      [{ uri: track.uri, id: track.id, durationMs: track.durationMs }],
      0,
      'single'
    );

    if (previewMode === 'hook') {
      // Hookmode: Play 15s preview
      playHookmodeTrack({ uri: track.uri, durationMs: track.durationMs });
    } else {
      // Normal mode: Play full track
      play.mutate({ uris: [track.uri] });
    }
  };

  // Hookmode playback helper function
  const playHookmodeTrack = (hookItem: { uri: string; durationMs: number }) => {
    const hookStart = Math.floor(hookItem.durationMs / 2 - 7500);
    const hookDuration = 15000;

    clearHookTimer();

    play.mutate({
      uris: [hookItem.uri],
      positionMs: Math.max(0, hookStart),
    });

    const timerId = setTimeout(() => {
      const nextItem = playbackQueue.moveNext();
      if (nextItem) {
        playHookmodeTrack(nextItem);
      } else {
        pause.mutate();
      }
    }, hookDuration);

    setHookTimer(timerId);
  };

  // Handle play button click
  // Note: usePlayerControls hook automatically shows DeviceSelector on NO_ACTIVE_DEVICE error
  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Activate player UI (enables polling)
    setIsPlayerActive(true);

    // Play from this track to end (preview mode determines playback style)
    const currentIndex = allTrackIds.findIndex((id) => id === track.instanceId);
    const queueItems = tracks.slice(currentIndex).map(t => ({
      uri: t.uri,
      id: t.id,
      durationMs: t.durationMs,
    }));

    if (previewMode === 'hook') {
      // Hookmode: we manage queue ourselves (isContextMode=false)
      playbackQueue.setQueue(queueItems, 0, 'from-track', false);
      playHookmodeTrack({ uri: track.uri, durationMs: track.durationMs });
    } else {
      // Smart playback: use contextUri+offset when list is unchanged (fast)
      const { activePlaylist, isOrderUnchanged } = useWorkbenchStore.getState();
      const playbackParams = buildSmartPlaybackParams(
        activePlaylist?.uri,
        currentIndex,
        queueItems.map(i => i.uri),
        isOrderUnchanged()
      );
      playbackQueue.setQueue(queueItems, 0, 'from-track', playbackParams.isContextMode);
      play.mutate(playbackParams);
    }
  };

  // Handle pause button click
  const handlePauseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    pause.mutate();
    clearHookTimer();
  };

  // Get album cover (smallest image)
  const albumCover = track.album.images?.[track.album.images.length - 1]?.url;

  // Get artist names
  const artistNames = track.artists.map((a) => a.name).join(', ');

  // Audio features are passed directly to CamelotKeyBadge which handles conversion

  // Grid column classes based on mode
  const gridCols = mode === 'history'
    ? 'grid-cols-[auto_140px_2fr_40px]' // checkbox, played_at, title, duration
    : 'grid-cols-[auto_auto_2fr_80px_60px_60px_40px_40px]'; // drag, position, title, bpm, key, energy, duration, actions

  // Handle history mode checkbox change
  const handleHistoryCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (onHistoryCheckboxToggle) {
      onHistoryCheckboxToggle(track.id);
    }
  };

  return (
    <>
      <div
        ref={setNodeRef}
        data-track-id={track.id}
        style={style}
        className={`
          group grid ${gridCols} gap-4 px-4 py-3
          rounded-md items-center text-sm transition-colors
          ${
            mode === 'history'
              ? (isHistorySelected
                  ? 'bg-emerald-500/20 hover:bg-emerald-500/30'
                  : 'hover:bg-zinc-900')
              : (selected
                  ? 'bg-emerald-500/20 hover:bg-emerald-500/30'
                  : 'hover:bg-zinc-900')
          }
          ${isSortableDragging ? 'cursor-grabbing z-50' : mode === 'history' ? 'cursor-default' : 'cursor-pointer'}
          ${isCurrentTrack ? 'bg-blue-500/20 border-l-4 border-blue-500' : ''}
          ${isJuggleSource ? 'border-2 border-orange-500 shadow-[0_0_12px_rgba(249,115,22,0.4)]' : ''}
        `}
        onClick={mode === 'history' ? undefined : handleClick}
        onDoubleClick={mode === 'history' ? undefined : handleDoubleClick}
        onContextMenu={mode === 'history' ? undefined : handleContextMenu}
      >
      {mode === 'history' ? (
        <>
          {/* History mode columns */}
          {/* Checkbox */}
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={isHistorySelected}
              onChange={handleHistoryCheckbox}
              className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-emerald-500 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
            />
          </div>

          {/* Played At */}
          <div className="text-zinc-400 text-sm">
            {playedAt ? formatPlayedAt(playedAt) : '—'}
          </div>

          {/* Album cover + Title + Artist */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex-shrink-0">
              {albumCover ? (
                <img
                  src={albumCover}
                  alt={track.album.name}
                  className="w-10 h-10 rounded object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-zinc-800" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <span className="font-medium text-zinc-100 truncate">
                  {track.name}
                </span>
              </div>
              <div className="text-xs text-zinc-500 truncate">{artistNames}</div>
            </div>
          </div>

          {/* Duration */}
          <div className="font-mono text-right text-zinc-400 text-xs">
            {formatDuration(track.durationMs)}
          </div>
        </>
      ) : (
        <>
          {/* Workbench mode columns */}
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4 text-zinc-500" />
          </div>

          {/* Position */}
          <div className="w-8 text-right font-mono text-zinc-500 text-xs">
            {position}
          </div>

          {/* Album cover + Title + Artist */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex-shrink-0">
              {albumCover ? (
                <img
                  src={albumCover}
                  alt={track.album.name}
                  className="w-10 h-10 rounded object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-zinc-800" />
              )}
              {/* Play/Pause button overlay on hover */}
              <button
                onClick={isCurrentTrack && isPlaying ? handlePauseClick : handlePlayClick}
                className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded"
                title={isCurrentTrack && isPlaying ? "Pause track" : "Play track"}
              >
                {isCurrentTrack && isPlaying ? (
                  <Pause className="w-5 h-5 text-white fill-white" />
                ) : (
                  <Play className="w-5 h-5 text-white fill-white" />
                )}
              </button>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <span className="font-medium text-zinc-100 truncate">
                  {track.name}
                </span>
                {capabilities.showLikeButton && (
                  <LikeButton
                    trackId={track.id}
                    isLiked={track.isLiked ?? false}
                    className={`flex-shrink-0 transition-opacity ${
                      track.isLiked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`}
                  />
                )}
              </div>
              <div className="text-xs text-zinc-500 truncate">{artistNames}</div>
            </div>
          </div>

          {/* BPM */}
          <div className="font-mono text-right text-zinc-300">
            {audioFeatures ? formatBpm(audioFeatures.tempo) : '—'}
          </div>

          {/* Camelot Key */}
          <div className="flex justify-center">
            {audioFeatures ? (
              <CamelotKeyBadge
                camelot={toCamelot(audioFeatures.key, audioFeatures.mode)}
                size="sm"
              />
            ) : (
              <span className="text-zinc-600 text-xs">—</span>
            )}
          </div>

          {/* Energy */}
          <div className="flex justify-center">
            {audioFeatures ? (
              <EnergyBar energy={audioFeatures.energy} width="w-12" />
            ) : (
              <span className="text-zinc-600 text-xs">—</span>
            )}
          </div>

          {/* Duration */}
          <div className="font-mono text-right text-zinc-400 text-xs">
            {formatDuration(track.durationMs)}
          </div>

          {/* Actions */}
          <div className="flex justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeTrack(track.instanceId);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-500/20 rounded"
              title="Remove track"
            >
              <X className="w-4 h-4 text-red-400" />
            </button>
          </div>
        </>
      )}
      </div>

      {/* Drop indicator - thin emerald line shown BELOW track when hovering */}
      {isDropOver && (
        <div className="relative w-full h-1 -mt-0.5 px-4">
          <div className="w-full h-0.5 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
        </div>
      )}

      {/* Context Menu Portal */}
      {showContextMenu && typeof document !== 'undefined' &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => {
                setShowContextMenu(false);
                setShowAskAISubmenu(false);
              }}
            />
            {/* Menu */}
            <div
              className="fixed w-56 py-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-[9999]"
              style={{
                top: `${menuPosition.y}px`,
                left: `${menuPosition.x}px`,
              }}
              role="menu"
              aria-label="Track actions menu"
            >
              <button
                onClick={handleCopyToClipboard}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
                role="menuitem"
              >
                <Copy className="w-4 h-4" />
                Copy to Clipboard
              </button>

              <hr className="my-1 border-zinc-700" />

              <button
                onClick={handlePasteSong}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
                role="menuitem"
              >
                <Music className="w-4 h-4" />
                Paste Song
              </button>

              <button
                onClick={handlePastePlaylist}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
                role="menuitem"
              >
                <ListMusic className="w-4 h-4" />
                Paste Playlist
              </button>

              <hr className="my-1 border-zinc-700" />

              {/* Ask AI Menu Item with Submenu */}
              <div
                className="relative"
                onMouseEnter={() => setShowAskAISubmenu(true)}
                onMouseLeave={() => setShowAskAISubmenu(false)}
              >
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
                  role="menuitem"
                  aria-haspopup="true"
                  aria-expanded={showAskAISubmenu}
                >
                  <Bot className="w-4 h-4 text-blue-500" />
                  <span>Ask AI</span>
                  <ChevronRight className="w-3 h-3 ml-auto" />
                </button>

                {/* Submenu (appears on hover) */}
                {showAskAISubmenu && (
                  <div className="absolute left-full top-0 ml-1 w-56 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden">
                    <button
                      onClick={() => handleAskAIWithCount(3)}
                      className="w-full px-4 py-2.5 text-left text-sm text-zinc-300 hover:bg-zinc-700"
                    >
                      Suggest 3 tracks
                    </button>
                    <button
                      onClick={() => handleAskAIWithCount(5)}
                      className="w-full px-4 py-2.5 text-left text-sm text-zinc-300 hover:bg-zinc-700"
                    >
                      Suggest 5 tracks
                    </button>
                    <button
                      onClick={() => handleAskAIWithCount(10)}
                      className="w-full px-4 py-2.5 text-left text-sm text-zinc-300 hover:bg-zinc-700"
                    >
                      Suggest 10 tracks
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}
